import { readMinutes } from '@iws/theme-schema/blog'
import { AlertCircle, Pencil } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'

import { getFileUrl } from '@/clients/http'
import { useBlogBody, useBlogPost } from '@/hooks/features/blog'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

import { BackButton, PageHeader } from '@/components/atoms'

import { BlogStatusBadge } from './BlogStatusBadge'

/** The body is merchant HTML; it renders in a sandboxed frame (no scripts, no same-origin access). */
const bodyDocument = (html: string) =>
  `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font:16px/1.7 system-ui,sans-serif;margin:0;padding:0 4px;color:CanvasText;background:Canvas}img{max-width:100%;height:auto}</style></head><body>${html}</body></html>`

export const BlogDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const post = useBlogPost(id)
  const body = useBlogBody(id, post.data?.content_id)
  const error = post.error ?? body.error

  return (
    <div className="mx-auto w-full max-w-[1088px] space-y-4 pb-10">
      <PageHeader
        size="large"
        title="Preview"
        back={<BackButton onClick={() => navigate('/blog')} label="Back to posts" />}
        actions={
          <Button variant="secondary" onClick={() => navigate(`/blog/${id}`)}>
            <Pencil className="size-4" />
            Edit post
          </Button>
        }
      />

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the post</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      ) : !post.data || body.isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card className="mx-auto max-w-3xl overflow-hidden">
          {post.data.banner?.path && (
            <img src={getFileUrl(post.data.banner.path)} alt="" className="aspect-video w-full object-cover" />
          )}
          <CardContent className="space-y-4 pt-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <BlogStatusBadge status={post.data.status} publishedAt={post.data.published_at} />
              <span>{new Date(post.data.created_at).toLocaleDateString(undefined, { dateStyle: 'long' })}</span>
              <span>· {readMinutes(body.data ?? '', post.data.read_duration)} min read</span>
            </div>
            <h2 className="text-heading-lg text-ink">{post.data.title}</h2>
            <iframe
              title={post.data.title}
              sandbox="allow-popups"
              srcDoc={bodyDocument(body.data ?? '')}
              className="h-[60vh] w-full border-0"
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
